import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

const variants = {
  success: 'text-green-800 bg-green-200',
  error: 'text-red-800 bg-red-200',
  warning: 'text-yellow-800 bg-yellow-200 ',
};

const fades = {
  left: {
    enter: 'animate-in fade-in slide-in-from-right-5 duration-300',
    leave: 'animate-out fade-out duration-150',
  },
  up: {
    enter: 'animate-in fade-in slide-in-from-bottom-5 duration-300',
    leave: 'animate-out fade-out duration-150',
  },
};

const LEAVE_DURATION_MS = 150;

export type ToastProps = React.AllHTMLAttributes<HTMLDivElement> & {
  title: string;
  description?: string;
  variant?: keyof typeof variants;
  icon?: 'error' | 'check' | 'edit_note';
  className?: string;
  fade?: 'left' | 'up';
  open: boolean;
  fixed?: boolean;
  position?: string;
};

export const Toast = ({
  title,
  description,
  variant = 'success',
  fade = 'up',
  className,
  open,
  icon,
  fixed,
  position,
}: ToastProps) => {
  const [mounted, setMounted] = useState(open);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setLeaving(false);
      return;
    }
    if (!mounted) return;
    setLeaving(true);
    const timer = setTimeout(() => {
      setMounted(false);
      setLeaving(false);
    }, LEAVE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [open, mounted]);

  if (!mounted) return null;

  return (
    <div
      className={clsx(
        'flex max-w-xl px-5 py-2.5 gap-2.5 rounded-md shadow-md z-999',
        leaving ? fades[fade].leave : fades[fade].enter,
        fixed && 'fixed',
        position && position,
        variants[variant],
        className
      )}
    >
      <div className="flex items-center">
        <span className="w-full h-full mt-0.5 mb-0 material-symbols-outlined">{icon}</span>
      </div>
      <div className="flex flex-col">
        <span className="font-bold">{title}</span>
        <span>{description}</span>
      </div>
    </div>
  );
};

Toast.displayName = 'Toast';
